import {
    ManagedAgentRequestModel,
    ManagedAgentResponseModel,
    McpResponseModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { cn } from '@/lib/utils';
import {
    Bot,
    Check,
    ChevronsUpDown,
    Info,
    Loader2,
    Pencil,
    Plus,
    Trash2,
    UploadCloud
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import {
    AutoConfirmControl,
    SkillToggleRows,
    ToggleRow,
    ToggleSection
} from './ToggleRows';
import { RlsNoticePill } from './RlsNoticePill';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MarkdownTextarea } from '@/components/ui/markdown-textarea';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';
import { SectionHeader } from '@/components/ui/section-header';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

import { useAuth } from '@/hooks/useAuth';

interface Props {
    mgmtApi: MgmtApi<unknown>;
}

interface AgentListRow {
    guid: string;
    agentId: string;
    displayName: string;
    organization: string;
    apiKey?: string | null;
    environmentId: string;
    vaultId?: string | null;
    isCoordinator: boolean;
    enableRls: boolean;
    userFacingDescription?: string | null;
    dbVersion?: number | null;
    liveName?: string | null;
    liveVersion?: number | null;
    liveLoading?: boolean;
    loadError?: boolean;
}

interface ModelItem {
    id: string;
    display_name: string;
    supports_effort?: boolean;
    effort_levels?: string[];
}

const EFFORT_LEVEL_LABELS: Record<string, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    xhigh: 'X-High',
    max: 'Max'
};

const EFFORT_TOOLTIP_TEXT =
    'How thoroughly Claude responds; lower effort saves tokens.';

const USER_FACING_DESCRIPTION_TOOLTIP_TEXT =
    'Shown to end users in the agents panel, agent picker and hover cards. Falls back to the description above when left empty.';

const BUILTIN_TOOLS = [
    { name: 'bash', description: 'Run shell commands in the sandbox' },
    { name: 'edit', description: 'Modify existing files' },
    { name: 'read', description: 'Read files from the workspace' },
    { name: 'write', description: 'Create new files' },
    { name: 'web_fetch', description: 'Fetch a specific URL' },
    { name: 'web_search', description: 'Search the public web' }
];

interface ToolState {
    enabled: boolean;
    auto: boolean;
}

interface McpServerState {
    name: string;
    url: string;
    auto: boolean;
}

const isMcpAppsUrl = (url: string): boolean => {
    try {
        return new URL(url).pathname.toLowerCase().includes('/mcp_apps');
    } catch {
        return url.toLowerCase().includes('/mcp_apps');
    }
};

interface SkillItem {
    skill_id: string;
    display_title: string;
    source: string;
}

interface SelectableResource {
    id: string;
    name: string;
    organization?: string | null;
}

interface AgentVersionItem {
    version: number;
    name: string;
    createdAt?: string | null;
    updatedAt?: string | null;
}

interface AgentForm {
    agentId: string; // '' when creating
    guid: string; // mgmt ManagedAgent record guid, '' when creating
    name: string;
    description: string;
    userFacingDescription: string;
    modelId: string;
    modelSpeed: 'standard' | 'fast';
    modelEffort: '' | 'low' | 'medium' | 'high' | 'xhigh' | 'max'; // '' = no override (agent default)
    system: string;
    environmentId: string;
    vaultId: string; // '' = none selected
    version: number | null; // which Anthropic agent version the DB should pin, null = latest
    isCoordinator: boolean;
    enableDashboards: boolean;
    tools: Record<string, ToolState>;
    mcpServers: McpServerState[];
    selectedSkills: string[]; // skill_ids
    apiKeySecret: string;
}

const makeEmptyTools = (): Record<string, ToolState> =>
    Object.fromEntries(
        BUILTIN_TOOLS.map((t) => [
            t.name,
            { enabled: t.name === 'read', auto: true }
        ])
    );

const makeEmptyForm = (): AgentForm => ({
    agentId: '',
    guid: '',
    name: '',
    description: '',
    userFacingDescription: '',
    modelId: '',
    modelSpeed: 'standard',
    modelEffort: '',
    system: '',
    environmentId: '',
    vaultId: '',
    version: null,
    isCoordinator: false,
    enableDashboards: true,
    tools: makeEmptyTools(),
    mcpServers: [],
    selectedSkills: [],
    apiKeySecret: ''
});

const formatDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleString() : '—';

export const AgentsTab: React.FC<Props> = ({ mgmtApi }) => {
    const { user } = useAuth();
    const organization = user?.metadata?.organization || '';
    const [agents, setAgents] = useState<AgentListRow[]>([]);
    const [dbAgents, setDbAgents] = useState<ManagedAgentResponseModel[]>([]);
    const [skills, setSkills] = useState<SkillItem[]>([]);
    const [models, setModels] = useState<ModelItem[]>([]);
    const [environments, setEnvironments] = useState<SelectableResource[]>([]);
    const [vaults, setVaults] = useState<SelectableResource[]>([]);
    const [mcpCatalog, setMcpCatalog] = useState<McpResponseModel[]>([]);
    const [versions, setVersions] = useState<AgentVersionItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deletingGuid, setDeletingGuid] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<{
        guid: string;
        agentId: string;
        apiKey?: string | null;
        label: string;
    } | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingExisting, setEditingExisting] = useState(false);
    const [form, setForm] = useState<AgentForm>(makeEmptyForm());
    const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
    const [uploadingSkill, setUploadingSkill] = useState(false);
    const [deletingSkillId, setDeletingSkillId] = useState<string | null>(null);
    const [skillToDelete, setSkillToDelete] = useState<string | null>(null);

    const fetchLiveInfo = async (
        row: ManagedAgentResponseModel
    ): Promise<Partial<AgentListRow>> => {
        const query = row.apiKey
            ? `?apiKeySecret=${encodeURIComponent(row.apiKey)}`
            : '';
        try {
            const res = await fetch(
                `/api/agents/${encodeURIComponent(row.agentId)}${query}`,
                { credentials: 'include' }
            );
            if (!res.ok) return { loadError: true };
            const c = await res.json();
            return {
                liveName: c.name,
                liveVersion: c.version
            };
        } catch {
            return { loadError: true };
        }
    };

    const fetchAgents = () => {
        setLoading(true);
        mgmtApi.api
            .v1ManagedAgentsList()
            .then((res) => {
                const mgmtAgents: ManagedAgentResponseModel[] =
                    res?.data?.data || [];
                setDbAgents(mgmtAgents);
                const nonCoordinators = mgmtAgents.filter(
                    (a) => !a.isCoordinator
                );
                const initialRows: AgentListRow[] = nonCoordinators.map(
                    (row) => ({
                        guid: row.guid,
                        agentId: row.agentId,
                        displayName: row.displayName,
                        organization: row.organization,
                        apiKey: row.apiKey,
                        environmentId: row.environmentId,
                        vaultId: row.vaultId,
                        isCoordinator: row.isCoordinator,
                        enableRls: !!row.enableRls,
                        userFacingDescription: row.userFacingDescription,
                        dbVersion: row.version,
                        liveLoading: true
                    })
                );
                setAgents(initialRows);
                setLoading(false);

                nonCoordinators.forEach((row) => {
                    fetchLiveInfo(row).then((liveInfo) => {
                        setAgents((current) =>
                            current.map((r) =>
                                r.guid === row.guid
                                    ? { ...r, ...liveInfo, liveLoading: false }
                                    : r
                            )
                        );
                    });
                });
            })
            .catch(() => {
                setAgents([]);
                setDbAgents([]);
                setLoading(false);
            });
    };

    const fetchSkills = () =>
        fetch('/api/skills', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => setSkills(Array.isArray(data) ? data : []))
            .catch(() => setSkills([]));

    useEffect(() => {
        fetchAgents();
        fetchSkills();
        fetch('/api/models', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => setModels(Array.isArray(data) ? data : []))
            .catch(() => setModels([]));
        fetch('/api/environments', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => setEnvironments(Array.isArray(data) ? data : []))
            .catch(() => setEnvironments([]));
        fetch('/api/vaults', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => setVaults(Array.isArray(data) ? data : []))
            .catch(() => setVaults([]));
        mgmtApi.api
            .v1McpsList()
            .then((res) => setMcpCatalog(res?.data?.data || []))
            .catch(() => setMcpCatalog([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (models.length === 0) return;
        setForm((f) => (f.modelId ? f : { ...f, modelId: models[0].id }));
    }, [models]);

    const resetForm = () => {
        setForm(makeEmptyForm());
        setVersions([]);
        setShowForm(false);
    };

    const openNewForm = () => {
        setForm(makeEmptyForm());
        setVersions([]);
        setEditingExisting(false);
        setShowForm(true);
    };

    const handleEditClick = async (agent: AgentListRow) => {
        setForm({
            ...makeEmptyForm(),
            agentId: agent.agentId,
            guid: agent.guid,
            name: agent.liveName || agent.displayName,
            environmentId: agent.environmentId || '',
            vaultId: agent.vaultId || '',
            version: agent.dbVersion ?? null,
            isCoordinator: agent.isCoordinator,
            userFacingDescription: agent.userFacingDescription || '',
            apiKeySecret: agent.apiKey || ''
        });
        setEditingExisting(true);
        setShowForm(true);

        const apiKeyQuery = agent.apiKey
            ? `?apiKeySecret=${encodeURIComponent(agent.apiKey)}`
            : '';

        // agents.update is full-replace, so this must be fully populated before any save.
        try {
            const res = await fetch(
                `/api/agents/${encodeURIComponent(
                    agent.agentId
                )}/full-config${apiKeyQuery}`,
                { credentials: 'include' }
            );
            if (res.ok) {
                const c = await res.json();
                const tools = makeEmptyTools();
                for (const [toolName, state] of Object.entries<any>(
                    c.builtinTools || {}
                )) {
                    if (tools[toolName]) {
                        // "Confirm" isn't handled by the runtime — normalize to auto.
                        tools[toolName] = {
                            enabled: !!state.enabled,
                            auto: true
                        };
                    }
                }
                setForm((f) => ({
                    ...f,
                    name: c.name ?? f.name,
                    description: c.description ?? '',
                    modelId: c.modelId || f.modelId,
                    modelSpeed: c.modelSpeed === 'fast' ? 'fast' : 'standard',
                    modelEffort: [
                        'low',
                        'medium',
                        'high',
                        'xhigh',
                        'max'
                    ].includes(c.modelEffort)
                        ? c.modelEffort
                        : '',
                    system: c.system ?? '',
                    enableDashboards: c.enableDashboards !== false,
                    tools,
                    mcpServers: Array.isArray(c.mcpServers)
                        ? c.mcpServers.map((s: any) => ({
                              name: s.name,
                              url: s.url,
                              auto: isMcpAppsUrl(s.url) ? s.auto !== false : true
                          }))
                        : [],
                    selectedSkills: Array.isArray(c.skills)
                        ? c.skills.map((s: any) => s.skill_id)
                        : []
                }));
            }
        } catch {}

        try {
            const res = await fetch(
                `/api/agents/${encodeURIComponent(
                    agent.agentId
                )}/versions${apiKeyQuery}`,
                { credentials: 'include' }
            );
            if (res.ok) {
                const list = await res.json();
                setVersions(Array.isArray(list) ? list : []);
            }
        } catch {
            setVersions([]);
        }
    };

    const setTool = (name: string, patch: Partial<ToolState>) =>
        setForm((f) => ({
            ...f,
            tools: {
                ...f.tools,
                [name]: { ...f.tools[name], ...patch }
            }
        }));

    // Ignores the query string: the backend appends ?organization=... to mcp_apps URLs after this picker's state is built, so a raw string compare would miss an already-added server.
    const normalizeMcpUrl = (url: string): string => {
        try {
            const u = new URL(url);
            const path = u.pathname.replace(/\/+$/, '');
            return `${u.protocol}//${u.host.toLowerCase()}${path}`;
        } catch {
            return url
                .split('?')[0]
                .split('#')[0]
                .replace(/\/+$/, '')
                .toLowerCase();
        }
    };

    const toggleMcpServer = (entry: McpResponseModel) => {
        const url = entry.externalUrl || entry.internalUrl;
        if (!url) return;
        const normalizedUrl = normalizeMcpUrl(url);
        setForm((f) => {
            const isAdded = f.mcpServers.some(
                (s) => normalizeMcpUrl(s.url) === normalizedUrl
            );
            return {
                ...f,
                mcpServers: isAdded
                    ? f.mcpServers.filter(
                          (s) => normalizeMcpUrl(s.url) !== normalizedUrl
                      )
                    : // entry.displayName becomes mcp_servers[].name on Anthropic's side and the citation badge text, so it must be saved here, not entry.name
                      [
                          ...f.mcpServers,
                          {
                              name: entry.displayName || entry.name,
                              url,
                              auto: true
                          }
                      ]
            };
        });
    };

    const setMcpServerAuto = (url: string, auto: boolean) =>
        setForm((f) => ({
            ...f,
            mcpServers: f.mcpServers.map((s) =>
                normalizeMcpUrl(s.url) === normalizeMcpUrl(url)
                    ? { ...s, auto }
                    : s
            )
        }));

    const toggleSkill = (skillId: string) =>
        setForm((f) => ({
            ...f,
            selectedSkills: f.selectedSkills.includes(skillId)
                ? f.selectedSkills.filter((s) => s !== skillId)
                : [...f.selectedSkills, skillId]
        }));

    const uploadSkillZip = async (file: File) => {
        const displayTitle = file.name.replace(/\.(skill|zip)$/i, '');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('displayTitle', displayTitle);
        const res = await fetch('/api/skills/upload', {
            method: 'POST',
            credentials: 'include',
            body: formData
        });
        if (!res.ok) {
            const j = await res.json().catch(() => null);
            throw new Error(j?.detail || 'Failed to upload skill');
        }
        await fetchSkills();
    };

    const handleSkillUpload = (file: File) => {
        setUploadingSkill(true);
        uploadSkillZip(file)
            .then(() => toast.success('Skill uploaded!'))
            .catch((e: any) =>
                toast.error(e?.message || 'Failed to upload skill')
            )
            .finally(() => setUploadingSkill(false));
    };

    const onSkillZipDrop = (accepted: File[], rejections: FileRejection[]) => {
        if (rejections.length > 0) {
            toast.error(
                rejections[0].errors[0]?.message ||
                    'Only .skill or .zip files are accepted'
            );
            return;
        }
        const file = accepted[0];
        if (file) handleSkillUpload(file);
    };

    const {
        getRootProps: getSkillDropRootProps,
        getInputProps: getSkillDropInputProps,
        isDragActive: isSkillDropActive
    } = useDropzone({
        onDrop: onSkillZipDrop,
        multiple: false,
        disabled: uploadingSkill,
        // .skill is Anthropic's packaged-skill format (a zip archive under a different extension).
        accept: {
            'application/zip': ['.zip'],
            'application/octet-stream': ['.skill']
        }
    });

    const handleDeleteSkill = (skillId: string) => {
        setDeletingSkillId(skillId);
        fetch(`/api/skills/${encodeURIComponent(skillId)}`, {
            method: 'DELETE',
            credentials: 'include'
        })
            .then((res) => {
                if (!res.ok) throw new Error('Failed to delete skill');
                setForm((f) => ({
                    ...f,
                    selectedSkills: f.selectedSkills.filter(
                        (s) => s !== skillId
                    )
                }));
                return fetchSkills();
            })
            .then(() => toast.success('Skill deleted.'))
            .catch((e: any) =>
                toast.error(e?.message || 'Failed to delete skill')
            )
            .finally(() => setDeletingSkillId(null));
    };

    const validate = (): boolean => {
        if (!form.name.trim()) {
            toast.error('Name is required');
            return false;
        }
        if (!form.description.trim()) {
            toast.error('Description is required');
            return false;
        }
        if (!form.system.trim()) {
            toast.error('System prompt is required');
            return false;
        }
        if (!form.environmentId) {
            toast.error('Environment is required');
            return false;
        }
        if (!form.vaultId) {
            toast.error('Vault is required');
            return false;
        }
        return true;
    };

    const buildRequestBody = () => ({
        name: form.name,
        description: form.description,
        modelId: form.modelId,
        modelSpeed: form.modelSpeed,
        modelEffort: form.modelEffort || null,
        system: form.system,
        enableDashboards: form.enableDashboards,
        builtinTools: form.tools,
        mcpServers: form.mcpServers,
        // A stale skill_id no longer in the catalog makes Anthropic reject the save outright, so drop it instead of resending.
        skills: form.selectedSkills
            .map((skillId) => skills.find((s) => s.skill_id === skillId))
            .filter((found): found is SkillItem => !!found)
            .map((found) => ({
                skill_id: found.skill_id,
                source: found.source || 'anthropic'
            })),
        apiKeySecret: form.apiKeySecret || undefined
    });

    const usesUserScopedMcp = form.mcpServers.some((server) =>
        mcpCatalog.some(
            (entry) =>
                entry.userScopedData &&
                [entry.externalUrl, entry.internalUrl].some(
                    (url) =>
                        !!url &&
                        normalizeMcpUrl(url) === normalizeMcpUrl(server.url)
                )
        )
    );

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            let agentId = form.agentId;
            let newVersion: number;
            if (editingExisting) {
                const res = await fetch(
                    `/api/agents/${encodeURIComponent(agentId)}/full-config`,
                    {
                        method: 'PUT',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(buildRequestBody())
                    }
                );
                if (!res.ok) {
                    const j = await res.json().catch(() => null);
                    throw new Error(j?.detail || 'Failed to update agent');
                }
                const saved = await res.json();
                newVersion = saved.version;
            } else {
                const res = await fetch('/api/agents', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(buildRequestBody())
                });
                if (!res.ok) {
                    const j = await res.json().catch(() => null);
                    throw new Error(j?.detail || 'Failed to create agent');
                }
                const saved = await res.json();
                agentId = saved.agentId;
                newVersion = saved.version;
            }

            const dbConfig: ManagedAgentRequestModel = {
                displayName: form.name,
                organization:
                    organization ||
                    dbAgents[0]?.organization ||
                    agents[0]?.organization ||
                    '',
                agentId,
                environmentId: form.environmentId,
                vaultId: form.vaultId || undefined,
                version: form.version ?? undefined,
                isCoordinator: form.isCoordinator,
                enableRls: usesUserScopedMcp,
                userFacingDescription:
                    form.userFacingDescription.trim() || undefined
            };
            if (editingExisting && form.guid) {
                await mgmtApi.api.v1ManagedAgentsUpdate(form.guid, dbConfig);
            } else {
                await mgmtApi.api.v1ManagedAgentsCreate(dbConfig);
            }

            resetForm();
            fetchAgents();
            toast.success(`Agent saved (version ${newVersion})!`);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to save agent');
        } finally {
            setSaving(false);
        }
    };

    // Deletes the mgmt DB row before archiving on Anthropic, since archiving is irreversible while the mgmt delete can safely fail and be retried.
    const handleArchive = async (target: {
        guid: string;
        agentId: string;
        apiKey?: string | null;
        label: string;
    }) => {
        setDeleteTarget(null);
        setDeletingGuid(target.guid);
        try {
            const mgmtRes = await fetch(
                `/mgmt/api/v1/ManagedAgents/${target.guid}`,
                {
                    method: 'DELETE',
                    credentials: 'include'
                }
            );
            if (!mgmtRes.ok) {
                const j = await mgmtRes.json().catch(() => null);
                throw new Error(
                    j?.detail ||
                        'Failed to delete agent — it may still be referenced by a Deep Intelligence configuration. Remove that reference first.'
                );
            }

            const apiKeyQuery = target.apiKey
                ? `?apiKeySecret=${encodeURIComponent(target.apiKey)}`
                : '';
            const archiveRes = await fetch(
                `/api/agents/${encodeURIComponent(
                    target.agentId
                )}${apiKeyQuery}`,
                { method: 'DELETE', credentials: 'include' }
            );
            if (!archiveRes.ok) {
                const j = await archiveRes.json().catch(() => null);
                throw new Error(
                    j?.detail ||
                        'Agent was removed from the list, but archiving it on Anthropic failed — it may still show up there.'
                );
            }

            if (showForm && form.guid === target.guid) {
                resetForm();
            }
            toast.success('Agent deleted.');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to delete agent');
        } finally {
            fetchAgents();
            setDeletingGuid(null);
        }
    };

    const handleDeleteClick = (agent: AgentListRow) =>
        setDeleteTarget({
            guid: agent.guid,
            agentId: agent.agentId,
            apiKey: agent.apiKey,
            label: agent.liveName || agent.displayName
        });

    // Not every model supports effort (e.g. Haiku) — Anthropic 400s if it's set on one that doesn't.
    const selectedModel = models.find((m) => m.id === form.modelId);
    const effortLevels = selectedModel?.effort_levels ?? [];
    const supportsEffort = !!selectedModel?.supports_effort;

    return (
        <section className="space-y-6">
            {!showForm && (
                <SectionHeader
                    title="Agents"
                    description="Create and edit Claude Agents — model, system prompt, tools, MCP servers and skills."
                    action={
                        <Button
                            onClick={openNewForm}
                            variant="tertiary"
                        >
                            <Plus />
                            New Agent
                        </Button>
                    }
                    className="items-end mb-6"
                />
            )}

            {!loading && showForm && (
                <SectionHeader
                    title={editingExisting ? 'Edit agent' : 'New agent'}
                    description="Model, system prompt, tools, MCP servers and skills for this agent."
                />
            )}

            {loading && <TableSkeleton columns={3} />}

            {!loading && showForm && (
                <div className="space-y-7">
                    <div className="gap-5 grid grid-cols-1 md:grid-cols-3">
                        <div className="space-y-2">
                            <div className="flex items-center h-5">
                                <Label htmlFor="agent-name">
                                    Name
                                    <span className="text-destructive"> *</span>
                                </Label>
                            </div>
                            <Input
                                id="agent-name"
                                value={form.name}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        name: e.target.value
                                    }))
                                }
                                placeholder="Research Assistant"
                                className="h-10"
                            />
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center h-5">
                                <Label>Model</Label>
                            </div>
                            <Popover
                                open={modelDropdownOpen}
                                onOpenChange={setModelDropdownOpen}
                            >
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="tertiary"
                                        role="combobox"
                                        aria-expanded={modelDropdownOpen}
                                        className="justify-between w-full h-10 font-normal text-sm"
                                    >
                                        {form.modelId ||
                                            (models.length === 0
                                                ? 'Loading models...'
                                                : 'Select model...')}
                                        <ChevronsUpDown className="opacity-50 ml-1 shrink-0" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="bg-background! p-0 w-(--radix-popover-trigger-width)"
                                    align="start"
                                >
                                    <Command className="bg-background!">
                                        <CommandInput placeholder="Search models..." />
                                        <CommandList className="max-h-64">
                                            <CommandEmpty>
                                                No models found.
                                            </CommandEmpty>
                                            <CommandGroup>
                                                {models.map((m) => (
                                                    <CommandItem
                                                        key={m.id}
                                                        value={`${m.id} ${m.display_name}`}
                                                        onSelect={() => {
                                                            setForm((f) => ({
                                                                ...f,
                                                                modelId: m.id,
                                                                modelEffort:
                                                                    f.modelEffort &&
                                                                    m.effort_levels?.includes(
                                                                        f.modelEffort
                                                                    )
                                                                        ? f.modelEffort
                                                                        : ''
                                                            }));
                                                            setModelDropdownOpen(
                                                                false
                                                            );
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                'mr-2 size-4 shrink-0',
                                                                form.modelId ===
                                                                    m.id
                                                                    ? 'opacity-100'
                                                                    : 'opacity-0'
                                                            )}
                                                        />
                                                        <span className="truncate">
                                                            {m.id}
                                                        </span>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center gap-1.5 h-5">
                                <Label htmlFor="effort-select">Effort</Label>
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Info className="size-3.5 text-muted-foreground cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                            <p>{EFFORT_TOOLTIP_TEXT}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <Select
                                disabled={!supportsEffort}
                                value={
                                    supportsEffort
                                        ? form.modelEffort || 'default'
                                        : 'unsupported'
                                }
                                onValueChange={(value) =>
                                    setForm((f) => ({
                                        ...f,
                                        modelEffort:
                                            value === 'default'
                                                ? ''
                                                : (value as AgentForm['modelEffort'])
                                    }))
                                }
                            >
                                <SelectTrigger id="effort-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {supportsEffort ? (
                                        <>
                                            <SelectItem value="default">
                                                Default
                                            </SelectItem>
                                            {effortLevels.map((level) => (
                                                <SelectItem
                                                    key={level}
                                                    value={level}
                                                >
                                                    {EFFORT_LEVEL_LABELS[
                                                        level
                                                    ] ?? level}
                                                </SelectItem>
                                            ))}
                                        </>
                                    ) : (
                                        <SelectItem value="unsupported">
                                            Not supported
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="agent-description">
                            Description
                            <span className="text-destructive"> *</span>
                        </Label>
                        <Textarea
                            id="agent-description"
                            value={form.description}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    description: e.target.value
                                }))
                            }
                            placeholder="A research assistant responsible for ..."
                            className="min-h-40"
                            spellCheck={false}
                        />
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center gap-1.5 h-5">
                            <Label htmlFor="agent-user-facing-description">
                                User-facing description
                            </Label>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Info className="size-3.5 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                        <p>
                                            {
                                                USER_FACING_DESCRIPTION_TOOLTIP_TEXT
                                            }
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        <MarkdownTextarea
                            id="agent-user-facing-description"
                            value={form.userFacingDescription}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    userFacingDescription: e.target.value
                                }))
                            }
                            placeholder="Answers questions about report and app usage, with charts where useful."
                            className="min-h-64 overflow-visible text-sm"
                            spellCheck={false}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="agent-system">
                            System prompt
                            <span className="text-destructive"> *</span>
                        </Label>
                        <Textarea
                            id="agent-system"
                            value={form.system}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    system: e.target.value
                                }))
                            }
                            placeholder="You are a research assistant..."
                            className="min-h-72"
                            spellCheck={false}
                        />
                    </div>

                    <div
                        className={cn(
                            'gap-5 grid grid-cols-1',
                            editingExisting
                                ? 'md:grid-cols-3'
                                : 'sm:grid-cols-2'
                        )}
                    >
                        <div className="space-y-2">
                            <Label>
                                Environment
                                <span className="text-destructive"> *</span>
                            </Label>
                            <Select
                                value={form.environmentId || undefined}
                                onValueChange={(value) =>
                                    setForm((f) => ({
                                        ...f,
                                        environmentId: value
                                    }))
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select environment" />
                                </SelectTrigger>
                                <SelectContent>
                                    {environments.map((env) => (
                                        <SelectItem key={env.id} value={env.id}>
                                            {env.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>
                                Vault
                                <span className="text-destructive"> *</span>
                            </Label>
                            <Select
                                value={form.vaultId || undefined}
                                onValueChange={(value) =>
                                    setForm((f) => ({
                                        ...f,
                                        vaultId: value
                                    }))
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select vault" />
                                </SelectTrigger>
                                <SelectContent>
                                    {vaults.map((v) => (
                                        <SelectItem key={v.id} value={v.id}>
                                            {v.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {editingExisting && (
                            <div className="space-y-2">
                                <Label>Version</Label>
                                <Select
                                    value={
                                        form.version === null
                                            ? 'latest'
                                            : String(form.version)
                                    }
                                    onValueChange={(value) =>
                                        setForm((f) => ({
                                            ...f,
                                            version:
                                                value === 'latest'
                                                    ? null
                                                    : Number(value)
                                        }))
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="latest">
                                            Always latest
                                        </SelectItem>
                                        {versions.map((v) => (
                                            <SelectItem
                                                key={v.version}
                                                value={String(v.version)}
                                            >
                                                v{v.version} —{' '}
                                                {formatDate(v.updatedAt)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    <ToggleSection title="Security">
                        <ToggleRow
                            id="agent-rls"
                            title="Row-level security"
                            description="Turned on automatically when the agent uses an MCP server with user-scoped data. Scheduled jobs with this agent must be private."
                            checked={usesUserScopedMcp}
                            onCheckedChange={() => {}}
                            disabled
                        />
                    </ToggleSection>

                    {editingExisting && versions.length > 0 && (
                        <div className="space-y-2">
                            {/* <div className="flex justify-between items-center">
                                <Label className="text-muted-foreground text-xs">
                                    Version history
                                </Label>
                                <span className="text-muted-foreground text-xs">
                                    Agent created{' '}
                                    {formatDate(versions[0]?.createdAt)}
                                </span>
                            </div> */}
                            <div className="border border-border rounded-lg overflow-hidden">
                                <Table
                                    containerClassName="scrollbar-hidden max-h-60 overflow-y-auto"
                                    className="border-separate border-spacing-0"
                                >
                                    <TableHeader className="top-0 z-10 sticky bg-background">
                                        <TableRow className="bg-muted/30 [&>th]:border-border [&>th]:border-b">
                                            <TableHead>Version</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Saved</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody className="[&_tr:not(:last-child)>td]:border-border [&_tr:not(:last-child)>td]:border-b">
                                        {versions.map((v) => (
                                            <TableRow key={v.version}>
                                                <TableCell className="font-mono text-sm">
                                                    v{v.version}
                                                    {form.version ===
                                                        v.version && (
                                                        <span className="ml-1.5 text-foreground">
                                                            (activated)
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-sm">
                                                    {v.name}
                                                </TableCell>
                                                <TableCell className="text-sm">
                                                    {formatDate(v.updatedAt)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}

                    <ToggleSection title="Built-in tools">
                        {BUILTIN_TOOLS.map((tool) => {
                            const state = form.tools[tool.name];
                            return (
                                <ToggleRow
                                    key={tool.name}
                                    id={`tool-${tool.name}`}
                                    title={tool.name}
                                    description={tool.description}
                                    checked={state.enabled}
                                    onCheckedChange={(checked) =>
                                        setTool(tool.name, { enabled: checked })
                                    }
                                />
                            );
                        })}
                    </ToggleSection>

                    <ToggleSection title="Dashboards">
                        <ToggleRow
                            id="agent-dashboards"
                            title="Dashboards"
                            description="Let this agent build and save dashboards for the user with the create_dashboard tool."
                            checked={form.enableDashboards}
                            onCheckedChange={(checked) =>
                                setForm((f) => ({ ...f, enableDashboards: checked }))
                            }
                        />
                    </ToggleSection>

                    <ToggleSection
                        title="MCP servers"
                        isEmpty={mcpCatalog.length === 0}
                        emptyText="No MCP servers registered"
                    >
                        {mcpCatalog.map((entry) => {
                            const entryUrl =
                                entry.externalUrl || entry.internalUrl;
                            const added = form.mcpServers.find(
                                (s) =>
                                    normalizeMcpUrl(s.url) ===
                                    normalizeMcpUrl(entryUrl || '')
                            );
                            return (
                                <ToggleRow
                                    key={entry.name}
                                    id={`mcp-${entry.name}`}
                                    title={entry.displayName || entry.name}
                                    checked={!!added}
                                    onCheckedChange={() =>
                                        toggleMcpServer(entry)
                                    }
                                    extra={
                                        <>
                                            {entry.userScopedData && (
                                                <RlsNoticePill />
                                            )}
                                            {added &&
                                                isMcpAppsUrl(added.url) && (
                                                    <AutoConfirmControl
                                                        auto={added.auto}
                                                        onChange={(auto) =>
                                                            setMcpServerAuto(
                                                                added.url,
                                                                auto
                                                            )
                                                        }
                                                    />
                                                )}
                                        </>
                                    }
                                />
                            );
                        })}
                    </ToggleSection>

                    <div className="space-y-3">
                        <SkillToggleRows
                            skills={skills}
                            selectedIds={form.selectedSkills}
                            onToggle={toggleSkill}
                            deletingSkillId={deletingSkillId}
                            skillToDelete={skillToDelete}
                            onSkillToDeleteChange={setSkillToDelete}
                            onDelete={handleDeleteSkill}
                        />

                        <div
                            {...getSkillDropRootProps()}
                            className={cn(
                                'flex items-center gap-2.5 p-3 border-2 border-dashed rounded-lg text-sm cursor-pointer transition-colors',
                                isSkillDropActive
                                    ? 'border-foreground bg-foreground/5'
                                    : 'border-muted-foreground/20 hover:border-foreground/40',
                                uploadingSkill &&
                                    'opacity-60 pointer-events-none'
                            )}
                        >
                            <input {...getSkillDropInputProps()} />
                            {uploadingSkill ? (
                                <Loader2 className="size-4 animate-spin" />
                            ) : (
                                <UploadCloud className="size-4" />
                            )}
                            <span className="text-muted-foreground">
                                Click to{' '}
                                <span className="text-foreground underline underline-offset-2">
                                    upload a custom skill
                                </span>{' '}
                                (.skill or .zip, with a SKILL.md at its root)
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-end items-center gap-3 pt-4 border-border border-t">
                        <Button onClick={resetForm} variant="tertiary">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </Button>
                    </div>
                </div>
            )}

            {!loading && !showForm && agents.length === 0 && (
                <EmptyState
                            icon={Bot}
                            title="No agents found"
                            description="Agents in your organization show up here"
                        />
            )}

            {!loading && !showForm && agents.length > 0 && (
                <div>
                    <Table variant="fluid">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Version</TableHead>
                                <TableHead className="w-px whitespace-nowrap">
                                    Actions
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {agents.map((agent) => (
                                <TableRow key={agent.guid}>
                                    <TableCell className="font-medium">
                                        {agent.loadError ? (
                                            <span className="text-amber-600 dark:text-amber-400">
                                                {agent.displayName} (kunne ikke
                                                hente live data)
                                            </span>
                                        ) : (
                                            agent.liveName || agent.displayName
                                        )}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {agent.liveLoading ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : agent.dbVersion != null ? (
                                            <>
                                                v{agent.dbVersion}
                                                {agent.liveVersion != null &&
                                                    agent.liveVersion !==
                                                        agent.dbVersion && (
                                                        <span className="ml-1.5 text-muted-foreground">
                                                            (v
                                                            {
                                                                agent.liveVersion
                                                            }{' '}
                                                            available)
                                                        </span>
                                                    )}
                                            </>
                                        ) : agent.liveVersion != null ? (
                                            <>
                                                Latest (v
                                                {agent.liveVersion})
                                            </>
                                        ) : (
                                            '—'
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="inline-flex gap-2">
                                            <Button
                                                variant="tertiary"
                                                size="default"
                                                onClick={() =>
                                                    handleEditClick(agent)
                                                }
                                                className="text-sm"
                                            >
                                                <Pencil />
                                                Edit
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="default"
                                                disabled={
                                                    deletingGuid === agent.guid
                                                }
                                                onClick={() =>
                                                    handleDeleteClick(agent)
                                                }
                                                className="text-sm"
                                            >
                                                {deletingGuid === agent.guid ? (
                                                    <Loader2 className="animate-spin" />
                                                ) : (
                                                    <Trash2 />
                                                )}
                                                Delete
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete agent "{deleteTarget?.label}"?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This archives the agent on Anthropic and removes it
                            from this list. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={() =>
                                    deleteTarget && handleArchive(deleteTarget)
                                }>
                                Delete permanently
                            </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
};
